import * as React from 'react';

import {
  type ColumnDef,
  type ColumnPinningState,
  type ColumnSizingState,
  flexRender,
  type Row,
} from '@tanstack/react-table';

import { TableCell, TableRow } from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { cn } from '@/lib/utils';

import type { RowAnimationPhase } from './hooks/use-row-enter-leave-animation';
import type { TableBodyBaseProps } from './types';
import { getColumnWidthStyle, isSelectColumn } from './utils/column-width-style';
import { buildRowMouseHandlers } from './utils/row-navigation';

/** Class hooks for the grow-in / shrink-out animation — the rules live in `index.css`. */
const ROW_ENTER_CLASS = 'data-table-row-enter';
const ROW_LEAVE_CLASS = 'data-table-row-leave';
const ANIMATION_GRID_CLASS = 'data-table-row-animation__grid';
const ANIMATION_CONTENT_CLASS = 'data-table-row-animation__content';

/**
 * Snapshot of every piece of table state a row's cells may read: its own selection, expansion
 * and selectability, plus which of its sub-rows are selected (parent cells derive
 * "some/all selected" and validation warnings from that). Cheap to build, and it lets the
 * memoized row skip re-rendering when some other row changed.
 */
export const getRowStateKey = <TData,>(row: Row<TData>): string =>
  `${row.getIsSelected() ? 1 : 0}${row.getIsExpanded() ? 1 : 0}${row.getCanSelect() ? 1 : 0}|` +
  row.subRows.map(subRow => (subRow.getIsSelected() ? 1 : 0)).join('');

/**
 * Table state that cells read but that is not part of the `row` object. Compared, not read: a
 * change in any of them means the memoized row must re-render.
 */
export interface RowMemoKeys<TData> {
  /** See `getRowStateKey`. */
  rowStateKey: string;
  /** Column definitions identity — a new set means cells must re-render even for the same row. */
  columnDefs: ColumnDef<TData, unknown>[];
  /** Ordered visible-leaf ids (see `virtualized-table-body`). */
  columnArrangementKey: string;
  columnSizing: ColumnSizingState;
  columnPinning: ColumnPinningState;
}

interface DataRowProps<TData>
  extends Omit<TableBodyBaseProps<TData>, 'table' | 'lastRowChildren' | 'getRowClassName'>,
    RowMemoKeys<TData> {
  row: Row<TData>;
  phase: RowAnimationPhase;
  /**
   * Wrap cell content in the animation grid. Kept for the row's whole life once true: adding or
   * removing the wrapper later would remount the cell content and drop focus from an input.
   */
  hasAnimationWrapper: boolean;
  rowClassName?: string;
  registerRowRef?: (rowId: string, el: HTMLTableRowElement | null) => void;
}

function DataRowComponent<TData>({
  row,
  phase,
  hasAnimationWrapper,
  rowClassName,
  filledColumnWidths,
  isScrolled,
  variant,
  tableId,
  onRowClick,
  getRowHref,
  registerRowRef,
}: DataRowProps<TData>) {
  const isLeaving = phase === 'leave';
  const isInteractive = !isLeaving && (onRowClick || getRowHref);

  return (
    <TableRow
      ref={registerRowRef && !isLeaving ? el => registerRowRef(row.id, el) : undefined}
      data-row-id={isLeaving ? undefined : row.id}
      data-state={!isLeaving && row.getIsSelected() && 'selected'}
      aria-hidden={isLeaving || undefined}
      className={cn(
        (onRowClick || getRowHref) && 'group cursor-pointer hover:bg-muted/50',
        phase === 'enter' && ROW_ENTER_CLASS,
        isLeaving && `${ROW_LEAVE_CLASS} pointer-events-none`,
        rowClassName
      )}
    >
      {row.getVisibleCells().map(cell => {
        const content = flexRender(cell.column.columnDef.cell, cell.getContext());

        return (
          <TableCell
            key={cell.id}
            variant={variant}
            style={{
              ...getCommonPinningStyles({ column: cell.column, withBorder: isScrolled }),
              ...getColumnWidthStyle({
                column: cell.column,
                size: cell.column.getSize(),
                tableId,
                filledColumnWidths,
              }),
            }}
            className={cn(cell.column.columnDef.meta?.cellClassName, rowClassName)}
            {...(isInteractive && !isSelectColumn(cell.column.id)
              ? buildRowMouseHandlers({ row: row.original, onRowClick, getRowHref })
              : {})}
          >
            {hasAnimationWrapper || isLeaving ? (
              // One-row grid: `grid-template-rows` animates between 0fr and 1fr without measuring,
              // and every cell shares the timing so the row moves as one piece.
              <div className={ANIMATION_GRID_CLASS}>
                <div className={ANIMATION_CONTENT_CLASS}>{content}</div>
              </div>
            ) : (
              content
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

/**
 * Memoized so a table state change (selecting a row, expanding a member) re-renders only the rows
 * it touched. TanStack keeps `row` identity stable across state changes, so identity alone would
 * keep stale cells — `RowMemoKeys` carries the state the cells actually read.
 */
export const DataRow = React.memo(DataRowComponent) as typeof DataRowComponent;
